import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Clock, Home, Calendar, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function PaymentSuccess() {
  const { user } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const [paymentStatus, setPaymentStatus] = useState('checking');
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [pollingAttempts, setPollingAttempts] = useState(0);

  const sessionId = searchParams.get('session_id');
  const maxAttempts = 10;
  const pollInterval = 2000; // 2 seconds

  useEffect(() => {
    if (sessionId && user) {
      checkPaymentStatus();
    } else if (!sessionId) {
      setPaymentStatus('error');
      toast.error('No payment session found');
    }
  }, [sessionId, user]);

  const checkPaymentStatus = async () => {
    if (pollingAttempts >= maxAttempts) {
      setPaymentStatus('timeout');
      toast.error('Payment verification timed out. Please check your bookings.');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/checkout/status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === 'paid') {
        setPaymentStatus('success');
        setPaymentDetails(data);
        toast.success('Payment successful! Your booking is confirmed.');
      } else if (data.status === 'expired') {
        setPaymentStatus('expired');
        toast.error('Payment session expired. Please try booking again.');
      } else {
        // Continue polling
        setPollingAttempts(prev => prev + 1);
        setTimeout(checkPaymentStatus, pollInterval);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      
      if (pollingAttempts >= maxAttempts - 1) {
        setPaymentStatus('error');
        toast.error('Unable to verify payment. Please contact support.');
      } else {
        setPollingAttempts(prev => prev + 1);
        setTimeout(checkPaymentStatus, pollInterval);
      }
    }
  };

  const renderStatusContent = () => {
    switch (paymentStatus) {
      case 'checking':
        return (
          <div style={{ textAlign: 'center', padding: '60px' }}>
            <Clock 
              size={64} 
              color="#10b981" 
              style={{ 
                margin: '0 auto 24px', 
                animation: 'spin 2s linear infinite' 
              }} 
            />
            <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '16px', color: '#2d3748' }}>
              Verifying Payment
            </h2>
            <p style={{ color: '#6b7280', marginBottom: '16px' }}>
              Please wait while we confirm your payment...
            </p>
            <div style={{ 
              background: '#e0f2fe', 
              padding: '12px', 
              borderRadius: '8px',
              fontSize: '0.875rem',
              color: '#0369a1'
            }}>
              Attempt {pollingAttempts + 1} of {maxAttempts}
            </div>
          </div>
        );

      case 'success':
        return (
          <div style={{ textAlign: 'center', padding: '60px' }}>
            <CheckCircle size={64} color="#10b981" style={{ margin: '0 auto 24px' }} />
            <h2 style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '16px', color: '#10b981' }}>
              Payment Successful! 🎉
            </h2>
            <p style={{ color: '#4a5568', fontSize: '1.1rem', marginBottom: '32px' }}>
              Your booking has been confirmed and you'll receive a confirmation email shortly.
            </p>

            {paymentDetails && (
              <div style={{ 
                background: '#f0fdf4', 
                padding: '24px', 
                borderRadius: '12px', 
                marginBottom: '32px',
                textAlign: 'left',
                maxWidth: '500px',
                margin: '0 auto 32px'
              }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '16px', color: '#166534' }}>
                  Payment Details
                </h3>
                <div style={{ display: 'grid', gap: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#4a5568' }}>Amount Paid:</span>
                    <span style={{ fontWeight: '600', color: '#2d3748' }}>
                      ${(paymentDetails.amount_total / 100).toFixed(2)} {paymentDetails.currency.toUpperCase()}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#4a5568' }}>Booking ID:</span>
                    <span style={{ fontWeight: '600', color: '#2d3748' }}>
                      #{paymentDetails.booking_id?.slice(-8)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#4a5568' }}>Payment Status:</span>
                    <span style={{ 
                      fontWeight: '600', 
                      color: '#10b981',
                      background: '#d1fae5',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontSize: '0.875rem'
                    }}>
                      Confirmed
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link 
                to="/bookings" 
                className="btn btn-primary"
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <Calendar size={20} />
                View My Bookings
              </Link>
              <Link 
                to="/dashboard" 
                className="btn btn-outline"
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <Home size={20} />
                Back to Dashboard
              </Link>
            </div>
          </div>
        );

      case 'expired':
        return (
          <div style={{ textAlign: 'center', padding: '60px' }}>
            <XCircle size={64} color="#ef4444" style={{ margin: '0 auto 24px' }} />
            <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '16px', color: '#dc2626' }}>
              Payment Session Expired
            </h2>
            <p style={{ color: '#6b7280', marginBottom: '32px' }}>
              Your payment session has expired. No charges were made to your account.
            </p>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link 
                to="/properties" 
                className="btn btn-primary"
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                Try Booking Again
              </Link>
              <Link 
                to="/dashboard" 
                className="btn btn-outline"
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <Home size={20} />
                Back to Dashboard
              </Link>
            </div>
          </div>
        );

      case 'timeout':
        return (
          <div style={{ textAlign: 'center', padding: '60px' }}>
            <Clock size={64} color="#f59e0b" style={{ margin: '0 auto 24px' }} />
            <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '16px', color: '#d97706' }}>
              Verification Timeout
            </h2>
            <p style={{ color: '#6b7280', marginBottom: '32px' }}>
              We couldn't verify your payment status. Please check your email for confirmation or contact support.
            </p>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link 
                to="/bookings" 
                className="btn btn-primary"
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <Calendar size={20} />
                Check My Bookings
              </Link>
              <button
                onClick={() => window.dispatchEvent(new CustomEvent('openChatbot'))}
                className="btn btn-outline"
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                💬 Contact Support
              </button>
            </div>
          </div>
        );

      case 'error':
      default:
        return (
          <div style={{ textAlign: 'center', padding: '60px' }}>
            <XCircle size={64} color="#ef4444" style={{ margin: '0 auto 24px' }} />
            <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '16px', color: '#dc2626' }}>
              Payment Verification Failed
            </h2>
            <p style={{ color: '#6b7280', marginBottom: '32px' }}>
              We encountered an error verifying your payment. Please contact our support team for assistance.
            </p>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={() => window.dispatchEvent(new CustomEvent('openChatbot'))}
                className="btn btn-primary"
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                💬 Contact Support
              </button>
              <Link 
                to="/dashboard" 
                className="btn btn-outline"
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <Home size={20} />
                Back to Dashboard
              </Link>
            </div>
          </div>
        );
    }
  };

  if (!user) {
    return (
      <div style={{ padding: '40px 0', minHeight: 'calc(100vh - 70px)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', padding: '60px' }}>
            <p style={{ color: '#718096', fontSize: '1.1rem' }}>
              Please log in to view your payment status.
            </p>
            <Link to="/auth" className="btn btn-primary" style={{ marginTop: '16px' }}>
              Sign In
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      padding: '40px 0', 
      minHeight: 'calc(100vh - 70px)', 
      background: '#f7fafc' 
    }}>
      <div className="container">
        <div style={{
          maxWidth: '600px',
          margin: '0 auto',
          background: 'white',
          borderRadius: '16px',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden'
        }}>
          {renderStatusContent()}
        </div>
      </div>

      <style jsx>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default PaymentSuccess;